/**
 * 이사 유형 화면 — v2 문서 버전.
 *
 * 프로덕션(`MovingTypesFlowAdapter`)은 스펙을 받아 FlowDocument 를 통째로 조립한다.
 * 유형 하나하나가 서브플로우를 물고 있어 어댑터 없이는 재현이 안 됐고, 프리뷰에는
 * 자리표시 화면만 있었다.
 *
 * 여기서는 데이터와 화면을 선언으로 적는다. 카드에 붙는 서브플로우는 derive 가
 * 스펙에서 만들어 `item.flow` 로 심는다 — 프로덕션과 같은 인라인 방식이라 별도의
 * flow 조회가 필요 없다.
 */
import type { AssetDocument, DataSource } from "@miso/design-system";

/** 호스트가 등록해야 하는 이름. */
export const MOVING_TYPE_REFS = {
  /** 이사 유형 스펙 목록. 호스트가 API 로 가져온다. */
  specs: "movingType.specs",
  /** 스펙 → item-picker 섹션(+ 인라인 서브플로우). 순수 변환이라 이 패키지가 갖는다. */
  pickerSections: "movingType.pickerSections",
} as const;

export const MOVING_TYPE_SECTIONS_DATA: DataSource[] = [
  { id: "specs", source: MOVING_TYPE_REFS.specs },
  {
    id: "sections",
    derive: MOVING_TYPE_REFS.pickerSections,
    // 이사일은 "양일이사" 안내 문구에 들어간다. 없으면 문구만 빠진다.
    args: { specs: { $var: "specs" }, movingDate: { $var: "movingDate" } },
  },
];

const doc: AssetDocument = {
  id: "counsel.movingTypes",
  schemaVersion: 2,
  initial: "pick",
  data: MOVING_TYPE_SECTIONS_DATA,
  steps: {
    pick: {
      kind: "screen",
      /*
       * 고른 유형과 그 상세 답이 intake 로 저장된다. 프로덕션은 `MovingTypesFlowAdapter`
       * 의 `onComplete` 에서 세션을 열고(`startIntakeSession`) 기록한다 — 세션이 이미
       * 열려 있으면 두 번째 호출은 호스트가 흘려보낸다.
       */
      onSubmit: [
        { do: "callAction", name: "startIntakeSession" },
        {
          do: "callAction",
          name: "recordMovingTypeIntake",
          args: { movingTypes: { $var: "movingTypes" } },
        },
        { do: "toast", tone: "success", text: "이사 유형이 저장되었습니다" },
        { do: "submit" },
      ],
      layout: {
        type: "Screen",
        props: { variant: "question", title: "이사 유형을 선택해주세요" },
        slots: {
          footer: [
            {
              type: "CtaButton",
              props: { label: "다음" },
              on: { onPress: [{ do: "submit" }] },
            },
          ],
        },
        children: [
          {
            type: "Card",
            visibleWhen: { isEmpty: { var: "specs" } },
            children: [
              {
                type: "Group",
                props: { padded: true, tight: true },
                children: [
                  {
                    type: "Text",
                    props: {
                      text: "이사 유형 스펙을 불러오지 못했습니다.",
                      strong: true,
                    },
                  },
                  {
                    type: "Text",
                    props: {
                      text: "프리뷰에 스펙 API 자격증명이 설정되지 않았을 수 있습니다.",
                      tone: "muted",
                    },
                  },
                ],
              },
            ],
          },
          {
            type: "ItemPicker",
            bind: "movingTypes",
            visibleWhen: { not: { isEmpty: { var: "specs" } } },
            props: {
              sections: { $var: "sections" },
              // 같은 유형을 여러 번 담을 수 있다(예: 경유지 두 곳).
              multiple: true,
              allowEmptyProceed: true,
              reviewTitle: "선택한 이사 유형",
              reviewButtonLabel: "이사 유형 확인",
              /*
               * 빈 상태 워딩은 도메인마다 다르다. 안 넘기면 짐 기록용 문구("선택한 짐이
               * 없어요")가 그대로 나온다.
               */
              emptyTitle: "선택한 이사유형이 없어요",
              emptyDescription:
                "선택한 이사유형이 없어요.\n이사유형 없이 진행하시겠어요?",
            },
          },
        ],
      },
      next: { kind: "exit" },
    },
  },
};

const initialValues = { movingTypes: [] };

export const MOVING_TYPES = { doc, initialValues };
